import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, History } from "lucide-react";
import Card from "./Card";
import { estateChangeTypeMeta } from "./AiEstateChanges";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { figureOrReason } from "../utils/aiFleetPortfolio";

/**
 * What changed in the AI estate over 30, 60 and 90 days, derived on read.
 *
 * Closed until asked for, because one read runs about 35 queries. Each figure keeps its limits on
 * its face: a null value renders its reason, never 0, and the coverage dates and caveats are in the
 * body rather than a tooltip. Windows match the spend forecast's, so every label names the quantity
 * as well as the window. Deliberately no chart: EstateGrowthChart draws the cumulative curve, and
 * these are windowed counts.
 */

const formatDate = (value) => new Date(value).toLocaleDateString("en-US", {
  year: "numeric", month: "short", day: "numeric", timeZone: "UTC",
});
const signed = (n) => (n > 0 ? `+${n}` : String(n));
// The score is stored as a 0-1 fraction; the module shows it in points.
const formatPoints = (fraction) => `${signed(Math.round(fraction * 1000) / 10)} pts`;
const formatHours = (hours) => `${hours.toFixed(1)} hours`;
const splitCamel = (key) => key.replace(/([a-z])([A-Z])/g, "$1 $2");

/** The recording marker when it falls inside the window: only then is the figure not a full-window count. */
const markerInside = (marker, delta, asOfUtc) => {
  if (!marker) return null;
  const at = new Date(marker);
  return at > new Date(delta.windowStart) && at < new Date(asOfUtc) ? marker : null;
};

function Note({ children }) {
  return <p className="mt-0.5 text-[11px] text-content-secondary">{children}</p>;
}

/** Value or reason, whichever the figure has. */
function Shown({ figure, format }) {
  const shown = figureOrReason(figure?.value, figure?.unavailableReason, format);
  return shown.kind === "value"
    ? <div className="text-lg font-semibold text-content-primary">{shown.text}</div>
    : <p className="text-xs text-content-secondary">{shown.text}</p>;
}

function Figure({ id, label, figure, format, annotated = false, children }) {
  const since = figure?.coveredSince;
  const until = figure?.coveredUntil;
  return (
    <div data-testid={`figure-${id}`}>
      <div className="text-xs font-medium text-content-secondary">{label}</div>
      <Shown figure={figure} format={format} />
      {children}
      {since && until && <Note>Covers {formatDate(since)} to {formatDate(until)}.</Note>}
      {since && !until && !annotated && <Note>Covers since {formatDate(since)}.</Note>}
      {figure?.caveat && <Note>{figure.caveat}</Note>}
    </div>
  );
}

function Breakdown({ id, label, breakdown, renderRow }) {
  const entries = breakdown?.value ? Object.entries(breakdown.value).sort((a, b) => b[1] - a[1]) : null;
  const reason = figureOrReason(breakdown?.value, breakdown?.unavailableReason);
  return (
    <div data-testid={`figure-${id}`}>
      <div className="text-xs font-medium text-content-secondary">{label}</div>
      {reason.kind === "reason" && <p className="text-xs text-content-secondary">{reason.text}</p>}
      {entries?.length === 0 && <p className="text-xs text-content-secondary">None recorded.</p>}
      {entries?.length > 0 && <ul className="mt-0.5 space-y-0.5">{entries.map(renderRow)}</ul>}
      {breakdown?.caveat && <Note>{breakdown.caveat}</Note>}
    </div>
  );
}

function CoverageLine({ delta }) {
  const covered = delta.daysCovered;
  const shown = figureOrReason(covered?.value, covered?.unavailableReason);
  return (
    <div data-testid="figure-daysCovered" className="text-xs text-content-secondary">
      {shown.kind === "value"
        ? <span className="font-medium text-content-primary">{shown.text} of {delta.windowDays} days covered</span>
        : shown.text}
      {covered?.caveat && <Note>{covered.caveat}</Note>}
    </div>
  );
}

function WindowColumn({ delta, marker, asOfUtc, onOpenType }) {
  const days = delta.windowDays;
  const label = (quantity) => `${quantity} — ${days} days`;
  // The marker on the label is what says these are not full-window counts (D3a).
  const since = markerInside(delta.governanceTransitions?.coveredSince ?? marker, delta, asOfUtc);
  const gated = (quantity) => (since ? `${label(quantity)}, since ${formatDate(since)}` : label(quantity));
  const remediated = figureOrReason(delta.risksRemediated?.value, delta.risksRemediated?.unavailableReason);

  return (
    <section data-testid={`estate-window-${days}`} className="space-y-3 rounded-md border border-border p-3">
      <h4 className="text-sm font-semibold text-content-primary">Last {days} days</h4>
      <CoverageLine delta={delta} />
      <Figure id="assetsAppeared" label={label("Assets appeared")} figure={delta.assetsAppeared} />
      <Breakdown
        id="assetsAppearedByKind"
        label={label("Assets appeared by kind")}
        breakdown={delta.assetsAppearedByKind}
        renderRow={([kind, count]) => (
          <li key={kind} className="flex justify-between text-xs text-content-primary">
            <span>{splitCamel(kind)}</span><span>{count}</span>
          </li>
        )}
      />
      <Figure id="risksOpened" label={label("Risks opened")} figure={delta.risksOpened} />
      <Figure id="risksClosed" label={label("Risks closed")} figure={delta.risksClosed}>
        <p className="text-xs text-content-primary">of which remediated: {remediated.text}</p>
        {delta.risksRemediated?.caveat && <Note>{delta.risksRemediated.caveat}</Note>}
      </Figure>
      <Figure id="meanHoursToResolve" label={label("Mean hours to resolve")} figure={delta.meanHoursToResolve} format={formatHours} />
      <Figure id="postureScoreMovement" label={label("Posture score movement")} figure={delta.postureScoreMovement} format={formatPoints} />
      <Figure id="assetCountMovement" label={label("Asset count movement")} figure={delta.assetCountMovement} format={signed} />
      <Breakdown
        id="changesByType"
        label={label("Estate change alerts by type")}
        breakdown={delta.changesByType}
        renderRow={([type, count]) => {
          const meta = estateChangeTypeMeta(type);
          const { Icon } = meta;
          return (
            <li key={type}>
              <button
                onClick={() => onOpenType?.({
                  alertType: type, windowDays: days, windowStart: delta.windowStart, asOfUtc,
                })}
                className="flex w-full items-center gap-1.5 rounded text-left text-xs text-content-primary hover:bg-theme-hover"
              >
                <Icon className={`w-3 h-3 shrink-0 ${meta.tone}`} />
                <span className="flex-1">{meta.label}</span>
                <span>{count}</span>
              </button>
            </li>
          );
        }}
      />
      <Figure id="governanceTransitions" label={gated("Governance transitions")} figure={delta.governanceTransitions} annotated={!!since} />
      <Figure id="statusTransitions" label={gated("Status transitions")} figure={delta.statusTransitions} annotated={!!since} />
      <Figure id="assetsGone" label={gated("Assets gone")} figure={delta.assetsGone} annotated={!!since} />
    </section>
  );
}

export default function AiEstateChangePanel({ onOpenType, refreshKey }) {
  const apiCall = useApi();
  const { canViewAllAiEstateData } = useRBAC();
  const org = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canView = canViewAllAiEstateData(org?.organizationId);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  // What the current data was read for: reopening does not read again unless a scan has finished
  // since, or the last read failed.
  const loaded = useRef({ done: false, key: undefined });
  const latest = useRef(0);

  useEffect(() => {
    if (!canView || !open) return;
    if (loaded.current.done && loaded.current.key === refreshKey) return;
    loaded.current = { done: true, key: refreshKey };
    const request = ++latest.current;
    setFailed(false);
    apiCall("GET", "/ai-assets/estate-changes")
      .then((result) => {
        if (request !== latest.current) return;
        if (!Array.isArray(result?.windows)) throw new Error("malformed estate changes response");
        setData(result);
      })
      .catch(() => {
        if (request !== latest.current) return;
        loaded.current = { done: false, key: undefined };
        setData(null);
        setFailed(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, open, refreshKey]);

  if (!canView) return null;

  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <Card className="p-4 mb-4">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left"
      >
        <Chevron className="w-4 h-4 text-content-secondary" />
        <History className="w-4 h-4 text-content-secondary" />
        <span className="text-sm font-semibold text-content-primary">Estate changes over 30, 60 and 90 days</span>
      </button>

      {open && (
        <div className="mt-3" data-testid="estate-change-panel">
          {failed && (
            <p className="text-sm text-content-secondary" data-testid="estate-change-unavailable">
              Estate changes over time are unavailable right now.
            </p>
          )}
          {!failed && !data && <p className="text-sm text-content-secondary">Loading…</p>}
          {!failed && data && (
            <>
              <div className="mb-3 space-y-1 text-xs text-content-secondary">
                <p>
                  These are counts of assets, risks and alerts, not spend. They share their 30, 60 and
                  90 day windows with the spend forecast, but the two cannot be compared or added.
                </p>
                <p data-testid="appeared-semantics">
                  Assets appeared counts assets that appeared in the window, including any now stale or
                  removed. The Estate growth chart counts only the currently active estate, so the two
                  30-day numbers can differ.
                </p>
                <p>As of {formatDate(data.asOfUtc)}. {data.scopeNote}</p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {data.windows.map((delta) => (
                  <WindowColumn
                    key={delta.windowDays}
                    delta={delta}
                    marker={data.transitionRecordingStartedAt}
                    asOfUtc={data.asOfUtc}
                    onOpenType={onOpenType}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
